/**
 * Rascunho do editor dos autos guardado no aparelho — a cópia de trabalho.
 *
 * O servidor guarda o rascunho do titular (Salvar e salvamento automático); o
 * aparelho guarda a cada alteração, para que recarregar a página, a queda da
 * conexão ou o fechamento acidental da aba não levem nada. O registro diz de que
 * revisão do servidor o texto partiu (`base`) e se há alteração ainda não
 * enviada (`pendente`): é o que decide, ao abrir, entre o texto daqui e o de lá,
 * e o que o PUT leva para detectar o conflito.
 *
 * A chave leva o usuário que está no aparelho (invariante do projeto: em máquina
 * compartilhada, o texto de um advogado não aparece para o próximo) e o titular
 * da conta em que ele atua, porque o rascunho é do titular.
 *
 * Sem usuário resolvido não há chave, e nada é lido nem gravado. O logout apaga
 * os rascunhos do usuário (`limparRascunhosDoUsuario`), junto com o outbox.
 */
import type { DocumentoForense } from '@/components/editor-forense';

const PREFIXO = 'jurisapp.editor.rascunho';

export interface RascunhoLocal {
  html: string;
  /** Papel, margens, timbrado… `null` = ainda o padrão. */
  documento: DocumentoForense | null;
  /** Revisão do servidor de que o texto partiu (0 = nenhuma). */
  base: number;
  /** Há alteração que o servidor ainda não recebeu. */
  pendente: boolean;
  /** Hora do aparelho (ms) da última gravação. */
  em: number;
}

/**
 * O registro mais novo de cada chave nesta visita, antes de a folga de gravação
 * levá-lo ao aparelho. O logout o esvazia — e uma gravação pendente cuja chave
 * não está mais aqui é de uma sessão encerrada e não acontece.
 */
export const rascunhoEmMemoria = new Map<string, RascunhoLocal>();

export function chaveDoRascunho(
  userId: string | undefined | null,
  titularId: string | undefined | null,
  processoId: string | undefined | null,
): string | null {
  if (!userId || !titularId || !processoId) return null;
  // O usuário vai por último, no mesmo formato de `chaveDePreferencia`
  // (`<prefixo>:<userId>`): é por esse sufixo que o logout acha as chaves.
  return `${PREFIXO}:${titularId}:${processoId}:${userId}`;
}

export function lerRascunhoLocal(chave: string | null): RascunhoLocal | null {
  if (!chave) return null;
  try {
    const bruto = localStorage.getItem(chave);
    if (!bruto) return null;
    const v = JSON.parse(bruto) as Partial<RascunhoLocal>;
    if (typeof v.html !== 'string' || typeof v.em !== 'number') return null;
    return {
      html: v.html,
      documento: v.documento && typeof v.documento === 'object' ? v.documento : null,
      // Sem `base`/`pendente`, o texto é do tempo em que o rascunho morava só aqui:
      // nunca foi ao servidor.
      base: typeof v.base === 'number' ? v.base : 0,
      pendente: typeof v.pendente === 'boolean' ? v.pendente : true,
      em: v.em,
    };
  } catch {
    // Armazenamento bloqueado ou valor corrompido: sem rascunho local.
    return null;
  }
}

/**
 * Grava o registro. Sem texto e sem nada a enviar, apaga a chave (um texto
 * apagado de propósito ainda precisa chegar ao servidor). Devolve false se o
 * aparelho recusou.
 */
export function gravarRascunhoLocal(chave: string | null, registro: Omit<RascunhoLocal, 'em'>): boolean {
  if (!chave) return false;
  try {
    if (registro.html || registro.pendente) {
      localStorage.setItem(chave, JSON.stringify({ ...registro, em: Date.now() } satisfies RascunhoLocal));
    } else {
      localStorage.removeItem(chave);
    }
    return true;
  } catch {
    // Cota cheia ou armazenamento bloqueado: o texto continua só na tela.
    return false;
  }
}

/** Apaga todos os rascunhos deste usuário no aparelho (logout). */
export function limparRascunhosDoUsuario(userId: string): void {
  rascunhoEmMemoria.clear();
  try {
    const alvo: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${PREFIXO}:`) && k.endsWith(`:${userId}`)) alvo.push(k);
    }
    alvo.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Armazenamento indisponível: não há o que limpar.
  }
}
